<div class="student-events-view">
  @if (error()) { <div class="alert alert-danger" role="alert">{{ error() }}</div> }
  @if (message()) { <div class="alert alert-success" role="status">{{ message() }}</div> }

  @if (!embedded) {
    <div class="header-row">
      <div>
        <h2>Events</h2>
        <p>Scholarship events, orientations and workshops. Sign up for what applies to you, and open a
          confirmed event for its full details. On the day you sign in from here.</p>
      </div>
    </div>
  }

  <div class="tabs">
    <button type="button" class="tab-btn" [class.active]="tab() === 'upcoming'"
      (click)="tab.set('upcoming')">Upcoming events</button>
    <button type="button" class="tab-btn" [class.active]="tab() === 'mine'"
      (click)="tab.set('mine')">My registrations</button>
    <button type="button" class="tab-btn" [class.active]="tab() === 'past'"
      (click)="tab.set('past')">Past events</button>
  </div>

  @if (tab() === 'upcoming') {
    <div class="card">
      <div class="card-body">
        @if (loading()) {
          <div class="empty-state">Loading your events…</div>
        } @else if (events().length === 0) {
          <div class="empty-state">
            <i class="fas fa-calendar-alt"></i>
            <h3>No upcoming events</h3>
            <p>Scholarship events and registration links will be listed here.</p>
          </div>
        } @else {
          <ul class="event-list">
            @for (event of events(); track event.id) {
              <li class="event-item">
                <div class="event-main">
                  <h3>
                    {{ event.title }}
                    @if (event.isMandatory) { <span class="status status-rejected">Mandatory</span> }
                  </h3>
                  <p class="muted">
                    <i class="fas fa-calendar"></i> {{ event.startDate | date: 'yyyy-MM-dd HH:mm' }}
                    @if (event.location) { · <i class="fas fa-map-marker-alt"></i> {{ event.location }} }
                    @if (event.sessionCount > 0) { · {{ event.sessionCount }} attendance option(s) }
                  </p>
                  @if (registrationFor(event); as registration) {
                    <span [class]="registrationClass(registration.status)">{{ registration.status }}</span>
                  }
                </div>
                <div class="event-actions">
                  <button type="button" class="btn btn-secondary btn-sm" (click)="open(event)"><i class="fas fa-eye"></i> View</button>
                  @if (canRegister(event)) {
                    <button type="button" class="btn btn-primary btn-sm" (click)="open(event)"><i class="fas fa-check"></i> Register</button>
                  }
                  @if (registrationFor(event); as registration) {
                    @if (canSignIn(registration)) {
                      <button type="button" class="btn btn-primary btn-sm" (click)="signIn(event.id)" [disabled]="busy()">
                        Sign in
                      </button>
                    }
                  }
                </div>
              </li>
            }
          </ul>
        }
      </div>
    </div>
  }

  @if (tab() === 'mine') {
    <div class="card">
      <div class="card-body">
        @if (activeRegistrations().length === 0) {
          <div class="empty-state">
            <i class="fas fa-calendar-check"></i>
            <h3>No registrations yet</h3>
            <p>Events you register for will appear here with their status.</p>
          </div>
        } @else {
          <table class="table">
            <thead>
              <tr>
                <th>Event</th>
                <th>When</th>
                <th>Option</th>
                <th>Status</th>
                <th>Attendance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (registration of activeRegistrations(); track registration.id) {
                <tr>
                  <td>{{ registration.eventTitle }}</td>
                  <td>{{ registration.eventStartDate | date: 'yyyy-MM-dd HH:mm' }}</td>
                  <td>{{ registration.sessionTitle || '—' }}</td>
                  <td>
                    <span [class]="registrationClass(registration.status)">{{ registration.status }}</span>
                    @if (registration.rejectionReason) {
                      <div class="muted">{{ registration.rejectionReason }}</div>
                    }
                  </td>
                  <td>
                    @if (registration.attendanceStatus) {
                      <span [class]="attendanceClass(registration.attendanceStatus)">{{ registration.attendanceStatus }}</span>
                    } @else {
                      <span class="muted">Not recorded</span>
                    }
                  </td>
                  <td class="row-actions">
                    @if (canWithdraw(registration)) {
                      <button type="button" class="btn btn-danger btn-sm" (click)="cancel(registration)" [disabled]="busy()">
                        Withdraw
                      </button>
                    }
                    @if (canSignIn(registration)) {
                      <button type="button" class="btn btn-primary btn-sm"
                        (click)="signIn(registration.eventId)" [disabled]="busy()">
                        Sign in
                      </button>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>
    </div>
  }

  @if (tab() === 'past') {
    <div class="card">
      <div class="card-body">
        @if (pastRegistrations().length === 0) {
          <div class="empty-state">
            <i class="fas fa-history"></i>
            <h3>No past events yet</h3>
            <p>Events you have attended — or missed — will be listed here with your attendance.</p>
          </div>
        } @else {
          <table class="table">
            <thead>
              <tr>
                <th>Event</th>
                <th>When</th>
                <th>Option</th>
                <th>Registration</th>
                <th>Attendance</th>
                <th>Signed in</th>
              </tr>
            </thead>
            <tbody>
              @for (registration of pastRegistrations(); track registration.id) {
                <tr>
                  <td>{{ registration.eventTitle }}</td>
                  <td>{{ registration.eventStartDate | date: 'yyyy-MM-dd HH:mm' }}</td>
                  <td>{{ registration.sessionTitle || '—' }}</td>
                  <td>
                    <span [class]="registrationClass(registration.status)">{{ registration.status }}</span>
                    @if (registration.rejectionReason) {
                      <div class="muted">{{ registration.rejectionReason }}</div>
                    }
                  </td>
                  <td>
                    <span [class]="attendanceClass(registration.attendanceStatus)">{{ attendanceLabel(registration) }}</span>
                    @if (registration.attendanceNotes) {
                      <div class="muted">{{ registration.attendanceNotes }}</div>
                    }
                  </td>
                  <td>{{ registration.signedInDate ? (registration.signedInDate | date: 'yyyy-MM-dd HH:mm') : '—' }}</td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>
    </div>
  }

  @if (selected(); as event) {
    <div class="card event-detail">
      <div class="card-header">
        {{ event.title }}
        <span [class]="statusClass(event.status)">{{ event.status }}</span>
        <button type="button" class="btn btn-secondary btn-sm panel-close" (click)="close()">Close</button>
      </div>
      <div class="card-body">
        @if (event.description) { <p>{{ event.description }}</p> }

        <div class="event-summary">
          <div>
            <span class="muted">When</span>
            <strong>{{ event.startDate | date: 'yyyy-MM-dd HH:mm' }} → {{ event.endDate | date: 'yyyy-MM-dd HH:mm' }}</strong>
          </div>
          <div><span class="muted">Venue</span><strong>{{ event.location || 'To be confirmed' }}</strong></div>
          @if (event.isMandatory) {
            <div><span class="muted">Attendance</span><strong>Compulsory</strong></div>
          }
        </div>

        @if (event.sessions.length > 0) {
          <div class="sessions">
            <h4>Attendance options</h4>
            @for (session of event.sessions; track session.id) {
              <label class="checkbox-line">
                @if (canRegister(event)) {
                  <input type="radio" name="session" [checked]="chosenSession() === session.id"
                    (change)="chosenSession.set(session.id)" />
                }
                <span>
                  {{ session.title || 'Option' }} — {{ session.startDate | date: 'yyyy-MM-dd HH:mm' }}
                  to {{ session.endDate | date: 'HH:mm' }}
                  @if (session.location) { · {{ session.location }} }
                </span>
              </label>
            }
          </div>
        }

        @if (isConfirmed(registrationFor(event))) {
          @if (event.meetingLink) {
            <div class="alert alert-success" role="status">
              Join online: <a [href]="event.meetingLink" target="_blank" rel="noopener">{{ event.meetingLink }}</a>
            </div>
          } @else {
            <div class="alert alert-success" role="status">Your place is confirmed.</div>
          }
        } @else if (registrationFor(event); as registration) {
          @if (registration.status === 'PENDING') {
            <div class="alert" role="status">
              Your registration is awaiting confirmation. The full details unlock once an administrator confirms it.
            </div>
          } @else if (registration.status === 'REJECTED') {
            <div class="alert alert-danger" role="alert">
              Your registration was not approved. {{ registration.rejectionReason || '' }}
            </div>
          }
        }

        <div class="form-actions">
          @if (canRegister(event)) {
            <button type="button" class="btn btn-primary" (click)="register()" [disabled]="busy()">
              <i class="fas fa-check"></i> Register
            </button>
          } @else if (registrationFor(event); as registration) {
            @if (canWithdraw(registration)) {
              <button type="button" class="btn btn-danger" (click)="cancel(registration)" [disabled]="busy()">
                Withdraw
              </button>
            }
            @if (canSignIn(registration)) {
              <button type="button" class="btn btn-primary" (click)="signIn(event.id)" [disabled]="busy()">
                <i class="fas fa-sign-in-alt"></i> Sign in on the day
              </button>
            }
          }
          <button type="button" class="btn btn-secondary" (click)="close()">Close</button>
        </div>
      </div>
    </div>
  }
</div>
